import { Check } from 'lucide-react';
import { useLang } from '../lib/lang';
import { IMAGES } from '../content/constants';
import { Page, HeroShell, Reveal } from '../components/motion';
import { SkeletonGrid, CTASection, PageSection, SectionHead } from '../components/blocks';

const FRAME_IMAGES = [IMAGES.serverBlue, IMAGES.fibre];

export default function CriticalPower() {
  const { t } = useLang();
  const c = t.power;
  return (
    <Page meta={{ ...t.meta.power, lang: t.lang }}>
      <HeroShell testId="power-hero" overline={c.hero.overline} lines={c.hero.lines} sub={c.hero.sub} image={IMAGES.substation} />

      <PageSection overline={c.why.overline} testId="power-why">
        <SkeletonGrid items={c.why.items} cols="md:grid-cols-2" testId="power-why-grid" />
      </PageSection>

      <PageSection overline={c.range.overline} title={c.range.title} testId="power-range" className="bg-surface/40">
        <Reveal>
          <div className="overflow-x-auto border border-white/10" data-testid="ups-range-table">
            <table className="w-full min-w-[640px] text-left font-mono text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.03]">
                  {c.range.cols.map((col, i) => (
                    <th key={i} className="px-6 py-4 text-[10px] font-bold uppercase tracking-[0.25em] text-volt">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {c.range.rows.map((row, i) => (
                  <tr key={i} data-testid={`ups-row-${i}`} className="border-b border-white/5 hover:bg-white/[0.04] transition-colors duration-300 group">
                    <td className="px-6 py-5 font-bold text-white group-hover:text-volt transition-colors duration-300 whitespace-nowrap">{row[0]}</td>
                    <td className="px-6 py-5 text-zinc-300 whitespace-nowrap">{row[1]}</td>
                    <td className="px-6 py-5 text-zinc-500">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {c.range.notes.map((n, i) => (
            <Reveal key={i} delay={i * 0.08} className="border-l-2 border-volt pl-6">
              <p className="font-display font-bold text-white">{n.t}</p>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{n.d}</p>
            </Reveal>
          ))}
        </div>
      </PageSection>

      <PageSection overline={c.frames.overline} testId="power-frames">
        <div className="space-y-20 md:space-y-28">
          {c.frames.items.map((f, i) => (
            <div key={i} className="grid lg:grid-cols-12 gap-10 items-center">
              <Reveal className={`lg:col-span-6 ${i % 2 ? 'lg:order-2' : ''} relative group`}>
                <div className="absolute -inset-10 spotlight" aria-hidden="true" />
                <img
                  src={FRAME_IMAGES[i]}
                  alt={f.name}
                  className="relative img-treated w-full aspect-[4/3] object-cover border border-white/10"
                />
                <span className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] bg-obsidian/80 backdrop-blur px-3 py-2 text-volt border border-white/10">
                  {f.name}
                </span>
              </Reveal>
              <div className={`lg:col-span-6 ${i % 2 ? 'lg:order-1' : ''}`}>
                <Reveal>
                  <h3 className="font-display text-3xl md:text-4xl font-black tracking-tight text-white">{f.name}</h3>
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.25em] text-volt">{f.tag}</p>
                  <ul className="mt-8 space-y-3.5">
                    {f.bullets.map((b, j) => (
                      <li key={j} className="flex gap-3 text-sm text-zinc-400 leading-relaxed">
                        <Check className="w-4 h-4 mt-0.5 shrink-0 text-volt" strokeWidth={3} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
        <Reveal className="mt-16">
          <p className="max-w-3xl text-sm text-zinc-500 leading-relaxed border-l-2 border-volt pl-6">{c.frames.note}</p>
        </Reveal>
      </PageSection>

      <section data-testid="power-batteries" className="border-t border-white/10 bg-surface/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <SectionHead overline={c.batteries.overline} title={c.batteries.title} />
            <Reveal delay={0.1}><p className="mt-6 text-base text-zinc-400 leading-relaxed max-w-2xl">{c.batteries.body}</p></Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5 relative group">
            <div className="absolute -inset-8 spotlight" aria-hidden="true" />
            <img src={IMAGES.circuit} alt="Battery systems" className="relative img-treated w-full aspect-[16/10] object-cover border border-white/10" />
          </Reveal>
        </div>
      </section>

      <PageSection overline={c.process.overline} testId="power-process">
        <SkeletonGrid items={c.process.steps} numbered cols="md:grid-cols-3 lg:grid-cols-5" testId="power-process-grid" />
      </PageSection>

      <CTASection title={c.cta} btn={c.cta} />
    </Page>
  );
}
